import { useState } from 'react'
import { useApi } from '../../../hooks/useApi'
import { RelocateApiService } from '../services/relocateApi'
import { ValidatePositionResponse } from '../types'
import { checkIfCommentsRequired } from '../utils/relocateUtils'

interface UseDestinyValidationProps {
  sharedState: {
    sku: string
    productOriginId: number | null
    originPositionType?: number
    posicionOrigen: string
    posicionDestino?: string
  }
  setSharedState: (updater: (prev: any) => any) => void
  onValidationSuccess: () => void
  relocateCommentaryEnabled: boolean
}

interface UseDestinyValidationReturn {
  localDestinyPosition: string
  setLocalDestinyPosition: (value: string) => void
  isValidating: boolean
  destinyValidated: boolean
  setDestinyValidated: (value: boolean) => void
  destinyValidationFailed: boolean
  error: string
  setError: (value: string) => void
  handleValidateDestiny: () => Promise<void>
  handleEditDestiny: () => void
}

export const useDestinyValidation = ({
  sharedState,
  setSharedState,
  onValidationSuccess,
  relocateCommentaryEnabled
}: UseDestinyValidationProps): UseDestinyValidationReturn => {
  // Inicializar con valores existentes de sharedState (para cuando se vuelve desde ConfirmationStep)
  const [localDestinyPosition, setLocalDestinyPosition] = useState(sharedState.posicionDestino || '')
  const [isValidating, setIsValidating] = useState(false)
  const [destinyValidated, setDestinyValidated] = useState(Boolean(sharedState.posicionDestino))
  const [destinyValidationFailed, setDestinyValidationFailed] = useState(false)
  const [error, setError] = useState('')

  const { callApi: validatePositionCallApi } = useApi<ValidatePositionResponse>()

  const handleValidateDestiny = async () => {
    if (!localDestinyPosition.trim()) {
      setError('Ingrese una posición de destino válida')
      return
    }

    if (localDestinyPosition.trim().toLowerCase() === sharedState.posicionOrigen.trim().toLowerCase()) {
      setError('La posición de destino debe ser diferente a la de origen')
      return
    }

    setIsValidating(true)
    setError('')
    setDestinyValidationFailed(false)

    try {
      const response = await validatePositionCallApi(RelocateApiService.validateDestinyPosition(
        localDestinyPosition.trim(),
        sharedState.sku,
        sharedState.productOriginId!
      ))

      if (response) {
        const destinyTypeId = response.typePositionId
        const originTypeId = sharedState.originPositionType
        const requiresComments = relocateCommentaryEnabled && checkIfCommentsRequired(originTypeId, destinyTypeId)

        setSharedState(prev => ({
          ...prev,
          posicionDestino: localDestinyPosition.trim(),
          destinyPositionId: response.positionId,
          destinyPositionType: destinyTypeId,
          requiresComments: Boolean(requiresComments)
        }))

        setDestinyValidated(true)

        if (requiresComments) {
          onValidationSuccess()
        }
      }
    } catch (err) {
      setError('Posición de destino no válida')
      setDestinyValidated(false)
      setDestinyValidationFailed(true)
    } finally {
      setIsValidating(false)
    }
  }

  const handleEditDestiny = () => {
    setDestinyValidated(false)
    setDestinyValidationFailed(false)
    setLocalDestinyPosition('')
    setSharedState(prev => ({
      ...prev,
      posicionDestino: '',
      destinyPositionId: undefined,
      destinyPositionType: undefined
    }))
    setError('')
  }

  return {
    localDestinyPosition,
    setLocalDestinyPosition,
    isValidating,
    destinyValidated,
    setDestinyValidated,
    destinyValidationFailed,
    error,
    setError,
    handleValidateDestiny,
    handleEditDestiny
  }
}